import { RootLayout } from '@/routes/__root'
import AuthCallbackRoute from '@/routes/_auth/callback'
import LoginRoute from '@/routes/_auth/login'
import AdminsRoute from '@/routes/_dashboard/admins'
import CustomerApplicationsRoute from '@/routes/_dashboard/customer-applications'
import CustomersRoute from '@/routes/_dashboard/customers'
import ErpCustomerFieldsRoute from '@/routes/_dashboard/erp/customer-fields'
import FormBuilderRoute from '@/routes/_dashboard/forms/$formId/edit'
import FormTemplateBuilderRoute from '@/routes/_dashboard/form-templates/$templateId/edit'
import FormTemplatesRoute from '@/routes/_dashboard/form-templates'
import FormsRoute from '@/routes/_dashboard/forms/index'
import DashboardHomeRoute from '@/routes/_dashboard/index'
import IntegrationsRoute from '@/routes/_dashboard/integrations'
import DashboardLayout from '@/routes/_dashboard/layout'
import QuestionGroupsRoute from '@/routes/_dashboard/question-groups'
import ReviewCenterRoute from '@/routes/_dashboard/review-center'
import ReviewNotificationsRoute from '@/routes/_dashboard/review-notifications'
import SalesRoute from '@/routes/_dashboard/sales'
import SettingsRoute from '@/routes/_dashboard/settings'
import SystemRoute from '@/routes/_dashboard/system'
import IndexRoute from '@/routes/index'
import LoginAdminRoute from '@/routes/login-admin'
import LoginSalesRoute from '@/routes/login-sales'
import DirectReviewApproveRoute from '@/routes/review-links/approve'
import DirectReviewQuestionnaireRoute from '@/routes/review-links/questionnaire'
import LoginSuperRoute, { LegacySuperLoginRedirect } from '@/routes/super-login'
import PublicFormRoute from '@/routes/to/$formId/index'
import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router'

const rootRoute = createRootRoute({ component: RootLayout })

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
})

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: AuthCallbackRoute,
})

const loginSuperRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-super',
  component: LoginSuperRoute,
})

const legacySuperLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/super-login',
  component: LegacySuperLoginRedirect,
})

const loginSalesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-sales',
  component: LoginSalesRoute,
})

const loginAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-admin',
  component: LoginAdminRoute,
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardLayout,
})

const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/',
  component: DashboardHomeRoute,
})

const formsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/forms',
  component: FormsRoute,
})

const questionGroupsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/question-groups',
  component: QuestionGroupsRoute,
})

const formTemplatesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/form-templates',
  component: FormTemplatesRoute,
})

const customersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/customers',
  component: CustomersRoute,
})

const customerApplicationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/customer-applications',
  component: CustomerApplicationsRoute,
})

const reviewCenterRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/reviews',
  component: ReviewCenterRoute,
})

const reviewCenterAliasRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/review-center',
  component: ReviewCenterRoute,
})

const reviewNotificationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/review-notifications',
  component: ReviewNotificationsRoute,
})

const salesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/sales',
  component: SalesRoute,
})

const adminsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/admins',
  component: AdminsRoute,
})

const formBuilderRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/forms/$formId/edit',
  component: FormBuilderRoute,
})

const formTemplateBuilderRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/form-templates/$templateId/edit',
  component: FormTemplateBuilderRoute,
})

const integrationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/integrations',
  component: IntegrationsRoute,
})

const settingsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/settings',
  component: SettingsRoute,
})

const systemRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system',
  component: SystemRoute,
})

const systemShareRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/share',
  component: SystemRoute,
})

const systemNavigationRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/navigation',
  component: SystemRoute,
})

const systemFormsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/forms',
  component: SystemRoute,
})

const systemCustomersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/customers',
  component: SystemRoute,
})

const systemRuntimeRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/system/runtime',
  component: SystemRoute,
})

const erpCustomerFieldsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/erp/customer-fields',
  component: ErpCustomerFieldsRoute,
})

const publicFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/to/$formId',
  component: PublicFormRoute,
})

const directReviewQuestionnaireRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review-links/questionnaire',
  component: DirectReviewQuestionnaireRoute,
})

const directReviewApproveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review-links/approve',
  component: DirectReviewApproveRoute,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loginSuperRoute,
  legacySuperLoginRoute,
  loginSalesRoute,
  loginAdminRoute,
  callbackRoute,
  dashboardRoute.addChildren([
    dashboardIndexRoute,
    formsRoute,
    formTemplatesRoute,
    questionGroupsRoute,
    customersRoute,
    customerApplicationsRoute,
    reviewCenterRoute,
    reviewCenterAliasRoute,
    reviewNotificationsRoute,
    salesRoute,
    adminsRoute,
    formBuilderRoute,
    formTemplateBuilderRoute,
    erpCustomerFieldsRoute,
    integrationsRoute,
    settingsRoute,
    systemRoute,
    systemNavigationRoute,
    systemShareRoute,
    systemFormsRoute,
    systemCustomersRoute,
    systemRuntimeRoute,
  ]),
  publicFormRoute,
  directReviewQuestionnaireRoute,
  directReviewApproveRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
